import { useState } from 'react'
import { Users, UserCheck, Settings2, Building2, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import TeamsManager from '@/components/organization/TeamsManager'
import UsersManager from '@/components/organization/UsersManager'
import RoutingSettingsPanel from '@/components/organization/RoutingSettingsPanel'
import { useTours } from '@/hooks/useTours'

export default function OrganizationSettings() {
  const [activeTab, setActiveTab] = useState<'teams' | 'users' | 'routing'>('teams')
  const { resetAllTours } = useTours()

  const handleResetTours = () => {
    resetAllTours()
    toast.success('Guías restablecidas. La guía del panel volverá a mostrarse sola la próxima vez.')
  }

  return (
    <div className="p-4 sm:p-6 overflow-x-hidden min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#075E54]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-[#075E54]" />
          </div>
          <div>
            <h1 data-tour="organization-main" className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
              Configuración de Organización
            </h1>
            <p className="text-xs sm:text-sm text-gray-600">
              Gestiona los equipos de atención comercial, miembros del staff y reglas de enrutamiento
            </p>
          </div>
        </div>
      </div>

      {/* Modern Contextual Pill Tabs */}
      <div className="border border-slate-200/80 bg-white/90 backdrop-blur rounded-2xl shadow-sm p-1.5 sm:p-2 mb-6">
        <nav
          className="flex space-x-1.5 sm:space-x-2 overflow-x-auto py-0.5 scrollbar-none"
          aria-label="Pestañas de Organización"
        >
          <button
            onClick={() => setActiveTab('teams')}
            className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs sm:text-sm font-semibold transition-all ${
              activeTab === 'teams'
                ? 'bg-[#075f55] text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Users className={`h-4 w-4 ${activeTab === 'teams' ? 'text-white' : 'text-slate-500'}`} />
            <span>Equipos de Atención</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs sm:text-sm font-semibold transition-all ${
              activeTab === 'users'
                ? 'bg-[#075f55] text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <UserCheck className={`h-4 w-4 ${activeTab === 'users' ? 'text-white' : 'text-slate-500'}`} />
            <span>Miembros del Staff</span>
          </button>
          <button
            onClick={() => setActiveTab('routing')}
            className={`flex items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-xs sm:text-sm font-semibold transition-all ${
              activeTab === 'routing'
                ? 'bg-[#075f55] text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Settings2 className={`h-4 w-4 ${activeTab === 'routing' ? 'text-white' : 'text-slate-500'}`} />
            <span>Reglas de Enrutamiento</span>
          </button>
        </nav>
      </div>

      {/* Reaprender guías: restablece el estado de "visto" de todos los tours. */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleResetTours}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075f55]"
        >
          <RotateCcw className="h-4 w-4" />
          Volver a mostrar la guía de bienvenida
        </button>
      </div>

      {/* Tab Content */}
      <div className="w-full">
        {activeTab === 'teams' && <TeamsManager />}
        {activeTab === 'users' && <UsersManager />}
        {activeTab === 'routing' && <RoutingSettingsPanel />}
      </div>
    </div>
  )
}
